import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ImageOff, Save, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { FEATURED_SLOTS, resolveImageUrl, useFeaturedImages } from '@/context/FeaturedImagesContext';
import { GALLERY_FILTERS } from '@/lib/site';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errorMessage';

const CAT_OPTIONS = GALLERY_FILTERS; // includes 'all'

export default function FeaturedPanel() {
    const { map, loading: featuredLoading, reload } = useFeaturedImages();
    const [gallery, setGallery] = useState([]);
    const [galleryLoading, setGalleryLoading] = useState(true);
    const [filterCat, setFilterCat] = useState('all');
    // Local pending selection per slot before save (preserves "unsaved" state UX)
    const [pending, setPending] = useState({});
    const [savingSlot, setSavingSlot] = useState(null);

    const loadGallery = async () => {
        setGalleryLoading(true);
        try {
            const res = await api.get('/gallery');
            setGallery(res.data || []);
        } catch {
            toast.error('Impossibile caricare la galleria');
        } finally {
            setGalleryLoading(false);
        }
    };
    useEffect(() => { loadGallery(); }, []);

    const filteredGallery = useMemo(() => {
        if (filterCat === 'all') return gallery;
        return gallery.filter((g) => g.category === filterCat);
    }, [gallery, filterCat]);

    const getSelectedId = (slot) => {
        if (pending[slot] !== undefined) return pending[slot];
        return map[slot]?.gallery_item_id || '';
    };

    const onSelect = (slot, value) => {
        setPending((p) => ({ ...p, [slot]: value }));
    };

    const save = async (slot) => {
        const id = getSelectedId(slot);
        if (!id) {
            toast.error('Seleziona una foto dalla galleria');
            return;
        }
        setSavingSlot(slot);
        try {
            await api.put(`/admin/featured/${slot}`, { gallery_item_id: id });
            toast.success('Immagine in evidenza aggiornata');
            setPending((p) => { const n = { ...p }; delete n[slot]; return n; });
            await reload();
        } catch (e) {
            toast.error(errorMessage(e, 'Errore salvataggio'));
        } finally {
            setSavingSlot(null);
        }
    };

    const clearSlot = async (slot) => {
        if (!window.confirm('Rimuovere la foto per questa sezione? Verrà mostrato il fallback elegante.')) return;
        setSavingSlot(slot);
        try {
            await api.delete(`/admin/featured/${slot}`);
            toast.success('Immagine rimossa');
            setPending((p) => { const n = { ...p }; delete n[slot]; return n; });
            await reload();
        } catch (e) {
            toast.error(errorMessage(e, 'Errore rimozione'));
        } finally {
            setSavingSlot(null);
        }
    };

    const slots = Object.entries(FEATURED_SLOTS);
    const galleryEmpty = !galleryLoading && gallery.length === 0;

    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={TEST_IDS.adminFeaturedPanel}>
            <div className="px-5 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-serif text-xl">Immagini in evidenza</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Scegli, per ogni sezione del sito, una foto della tua galleria. Se non assegni nulla, viene mostrato un fallback elegante con i colori del brand.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Label className="eyebrow text-muted-foreground">Filtra galleria</Label>
                    <Select value={filterCat} onValueChange={setFilterCat}>
                        <SelectTrigger className="w-44 h-9" data-testid="admin-featured-filter-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {CAT_OPTIONS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {galleryEmpty && (
                <div className="mx-5 mt-5 rounded-md border border-dashed border-border bg-muted/40 p-5 text-sm text-muted-foreground flex items-start gap-3">
                    <ImageOff className="h-5 w-5 mt-0.5 shrink-0" />
                    <div>
                        <p className="font-medium text-foreground">La galleria è vuota.</p>
                        <p className="mt-1">Carica prima delle foto nella tab “Galleria”, poi torna qui per assegnarle alle sezioni del sito.</p>
                    </div>
                </div>
            )}

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                {slots.map(([slotKey, label]) => {
                    const current = map[slotKey];
                    const selectedId = getSelectedId(slotKey);
                    const selectedItem = selectedId ? gallery.find((g) => g.id === selectedId) : null;
                    const previewUrl = selectedItem?.image_url || current?.image_url || null;
                    const isDirty = pending[slotKey] !== undefined && pending[slotKey] !== (current?.gallery_item_id || '');
                    const isSaving = savingSlot === slotKey;

                    return (
                        <div
                            key={slotKey}
                            data-testid={TEST_IDS.adminFeaturedSlotRow}
                            data-slot={slotKey}
                            className="rounded-lg border border-border bg-background/60 p-4"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="font-medium text-sm leading-tight">{label}</p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">{slotKey}</p>
                                </div>
                                <span
                                    className={cn(
                                        'inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full',
                                        current?.image_url
                                            ? 'bg-atelier-sage/20 text-atelier-sage-ink'
                                            : 'bg-secondary text-muted-foreground',
                                    )}
                                >
                                    {current?.image_url ? <Check className="h-3 w-3" /> : <ImageOff className="h-3 w-3" />}
                                    {current?.image_url ? 'Assegnata' : 'Fallback'}
                                </span>
                            </div>

                            <div className="mt-3 grid grid-cols-[96px_1fr] gap-3 items-start">
                                <div className="relative aspect-square rounded-md overflow-hidden bg-muted border border-border">
                                    {previewUrl ? (
                                        <img
                                            src={resolveImageUrl(previewUrl)}
                                            alt={selectedItem?.title || current?.title || label}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 bg-gradient-to-br from-atelier-cream via-atelier-beige to-atelier-sage/55" />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <Select
                                        value={selectedId || ''}
                                        onValueChange={(v) => onSelect(slotKey, v)}
                                        disabled={galleryLoading || galleryEmpty}
                                    >
                                        <SelectTrigger
                                            className="h-9 text-sm"
                                            data-testid={`${TEST_IDS.adminFeaturedSlotSelect}-${slotKey}`}
                                        >
                                            <SelectValue placeholder={galleryLoading ? 'Caricamento…' : 'Scegli una foto…'} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {filteredGallery.length === 0 && (
                                                <div className="px-2 py-2 text-xs text-muted-foreground">Nessuna foto in questa categoria.</div>
                                            )}
                                            {filteredGallery.map((g) => (
                                                <SelectItem key={g.id} value={g.id}>
                                                    <span className="flex items-center gap-2">
                                                        <span className="capitalize text-muted-foreground text-[11px]">[{g.category}]</span>
                                                        <span className="truncate max-w-[220px]">{g.title}</span>
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        <Button
                                            size="sm"
                                            onClick={() => save(slotKey)}
                                            disabled={!selectedId || !isDirty || isSaving || featuredLoading}
                                            className="h-8 px-3"
                                            data-testid={`${TEST_IDS.adminFeaturedSlotSave}-${slotKey}`}
                                        >
                                            {isSaving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                                            {isDirty ? 'Salva' : 'Salvato'}
                                        </Button>
                                        {current?.gallery_item_id && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => clearSlot(slotKey)}
                                                disabled={isSaving}
                                                className="h-8 px-3 text-muted-foreground"
                                                data-testid={`${TEST_IDS.adminFeaturedSlotClear}-${slotKey}`}
                                            >
                                                <X className="h-3.5 w-3.5 mr-1.5" /> Rimuovi
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
